import React, { useState, useEffect, useContext, useRef } from "react";

// Context
import { useProducts } from "../context/ProductsContext";
import AlertContext from "../context/AlertContext";

// API
import { getHeroCatalogue } from "../api/product";

// Bootstrap components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";

// Util
import { toggleClass } from "../utils/toggleClass";

// Custom Components
import ProductCard from "../components/ProductCard/ProductCard";
import CustomPagination from "../components/Pagination/Pagination";
import PriceRangeSlider from "../components/PriceRangeSlider/PriceRangeSlider";

// Assets
import FilterIcon from "../assets/images/filter-icon.svg";

export default function Catalogue() {
  const [products, setProducts] = useState(null);
  const [pageHeroBackground, setPageHeroBackground] = useState({});

  const priceChangeTimeoutRef = useRef(null);

  const alert = useContext(AlertContext);



  const [loading, setLoading] = useState(false);
  const [loadingHero, setLoadingHero] = useState(true);



  const [originalProducts, setOriginalProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Store all products for filtering

  const [currentPage, setCurrentPage] = useState(1);

  const [categories, setCategories] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 });
  const [resetFilters, setResetFilters] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredTotalPages, setFilteredTotalPages] = useState(1);

  // NEW: Caratula and Carcasa filter states
  const [caratulaFilters, setCaratulaFilters] = useState([]);
  const [carcasaFilters, setCarcasaFilters] = useState([]);

  // ADD: Mobile filter state
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // ADD: Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 991);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);



  // NEW: Extract all filter options from products data
  const getUniqueBrands = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueBrands = [...new Set(allProducts.map(product => product.marca?.nombre).filter(Boolean))];
    return uniqueBrands.sort();
  };

  const getUniqueWatchBands = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueBands = [...new Set(allProducts.map(product => product.tipoCorrea?.nombre).filter(Boolean))];
    return uniqueBands.sort();
  };

  const getUniqueWatchGenres = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueGenres = [...new Set(allProducts.map(product => product.genero?.nombre).filter(Boolean))];
    return uniqueGenres.sort();
  };

  const getUniqueWatchTypes = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueTypes = [...new Set(allProducts.map(product => product.tipo?.nombre).filter(Boolean))];
    return uniqueTypes.sort();
  };

  const getUniqueWatchCalibers = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueCalibers = [...new Set(allProducts.map(product => product.calibre?.nombre).filter(Boolean))];
    return uniqueCalibers.sort();
  };

  const getUniqueWatchConditions = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueConditions = [...new Set(allProducts.map(product => product.condicion?.nombre).filter(Boolean))];
    return uniqueConditions.sort();
  };

  const getUniqueCaratulas = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueCaratulas = [...new Set(allProducts.map(product => product.colorCaratula?.color).filter(Boolean))];
    return uniqueCaratulas.sort();
  };

  const getUniqueCarcasas = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueCarcasas = [...new Set(allProducts.map(product => product.materialCarcasa?.material).filter(Boolean))];
    return uniqueCarcasas.sort();
  };



  // Use products from context instead of direct API calls
  const { fetchFullProducts } = useProducts();

  // Fetch all products once and handle pagination client-side
  useEffect(() => {
    const initializeProducts = async () => {
      setLoading(true);

      try {
        // Always use ProductsContext to ensure country filtering is applied
        const productsData = await fetchFullProducts();

        // Always update products state, even if empty
        setAllProducts(productsData || []);

        if (productsData && productsData.length > 0) {
          // Set initial products (first 12)
          const initialProducts = productsData.slice(0, 12);
          setProducts(initialProducts);
          setOriginalProducts(initialProducts);

          // Calculate total pages based on all products
          const totalPages = Math.ceil(productsData.length / 12);
          setTotalPages(totalPages);
        } else {
          // No products available - clear all product states
          setProducts([]);
          setOriginalProducts([]);
          setFilteredProducts([]);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeProducts();
  }, [fetchFullProducts]); // Only run when fetchFullProducts changes (when country changes)


  // Handle page changes for unfiltered products
  useEffect(() => {
    const isFiltering = categories.length > 0 || priceRange.min > 0 || priceRange.max < 100000;

    if (allProducts.length > 0 && !isFiltering) {
      const itemsPerPage = 12;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const pageProducts = allProducts.slice(startIndex, endIndex);
      setProducts(pageProducts);
      setOriginalProducts(pageProducts);
    }
  }, [currentPage, allProducts, categories, priceRange]);

  // Filter products whenever categories, price range, caratula, or carcasa filters change
  useEffect(() => {
    if (allProducts.length > 0) {
      filterByReference();
    }
  }, [categories, priceRange, caratulaFilters, carcasaFilters, allProducts]);

  const filterByReference = () => {
    let filtered = allProducts; // Filter ALL products

    // Filter by categories
    if (categories.length > 0) {
      filtered = filtered.filter((product) => {
        return categories.some((category) => {
          return (
            category === product.genero?.nombre.toLowerCase() ||
            category === product.tipoCorrea?.nombre.toLowerCase() ||
            category === product.marca?.nombre.toLowerCase() ||
            category === product.tipo?.nombre.toLowerCase() ||
            category === product.calibre?.nombre.toLowerCase() ||
            category === product.condicion?.nombre.toLowerCase()
          );
        });
      });
    }

    // NEW: Filter by caratula (dial color)
    if (caratulaFilters.length > 0) {
      filtered = filtered.filter((product) => {
        return caratulaFilters.some((caratulaFilter) => {
          return caratulaFilter === product.colorCaratula?.color;
        });
      });
    }

    // NEW: Filter by carcasa (case material)
    if (carcasaFilters.length > 0) {
      filtered = filtered.filter((product) => {
        return carcasaFilters.some((carcasaFilter) => {
          return carcasaFilter === product.materialCarcasa?.material;
        });
      });
    }

    // Filter by price range
    if (allProducts && allProducts.length >= 5) {
      filtered = filtered.filter((product) => {
        const price = product.precio || product.price || 0;
        const numericPrice =
          typeof price === "string"
            ? parseFloat(price.replace(/[^0-9.-]+/g, ""))
            : price;

        return numericPrice >= priceRange.min && numericPrice <= priceRange.max;
      });
    }

    // Store all filtered products and calculate pagination
    setFilteredProducts(filtered);
    const itemsPerPage = 12;
    const totalFilteredPages = Math.ceil(filtered.length / itemsPerPage);
    setFilteredTotalPages(totalFilteredPages);
    setCurrentPage(1); // Reset to first page when filtering

    console.log('Filtering results:', {
      totalProducts: allProducts.length,
      filteredProducts: filtered.length,
      categories,
      priceRange,
      totalFilteredPages
    });
  };

  // UPDATED: Clear filters with mobile state
  const clearFilters = () => {
    const selectedFilters = document.querySelectorAll(
      ".filters__filter__dropdown-menu__item__name.active"
    );
    selectedFilters.forEach((filter) => filter.classList.remove("active"));
    setCategories([]);
    setCaratulaFilters([]);
    setCarcasaFilters([]);
    setPriceRange({ min: 0, max: 100000 });
    setResetFilters((prev) => prev + 1);
    setCurrentPage(1); // Reset to first page when clearing filters

    // Auto-close on mobile when clearing
    if (isMobile) {
      setIsMobileFilterOpen(false);
    }
  };

  // ADD: Mobile filter toggle handler
  const handleMobileFilterToggle = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);

    // Clear any pending auto-collapse when user manually opens
    if (!isMobileFilterOpen && priceChangeTimeoutRef.current) {
      clearTimeout(priceChangeTimeoutRef.current);
    }
  };

  // Cleanup function
  useEffect(() => {
    return () => {
      if (priceChangeTimeoutRef.current) {
        clearTimeout(priceChangeTimeoutRef.current);
      }
    };
  }, []);

  // UPDATED: Filter click with mobile auto-collapse
  const handleFilterClick = (filterName, filterType, event) => {
    event.target.classList.toggle("active");

    const filterValue = filterName.toLowerCase();

    if (categories.includes(filterValue)) {
      setCategories(categories.filter((category) => category !== filterValue));
    } else {
      setCategories([...categories, filterValue]);
    }

    // Auto-collapse on mobile for category selections
    if (isMobile && isMobileFilterOpen) {
      setTimeout(() => {
        setIsMobileFilterOpen(false);

        // Quick scroll to products
        setTimeout(() => {
          const productsSection = document.querySelector(
            ".products__product-container"
          );
          if (productsSection) {
            productsSection.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }, 600);
    }
  };

  // NEW: Handle caratula filter clicks
  const handleCaratulaFilterClick = (caratulaValue, event) => {
    event.target.classList.toggle("active");

    if (caratulaFilters.includes(caratulaValue)) {
      setCaratulaFilters(caratulaFilters.filter((filter) => filter !== caratulaValue));
    } else {
      setCaratulaFilters([...caratulaFilters, caratulaValue]);
    }

    // Auto-collapse on mobile for caratula selections
    if (isMobile && isMobileFilterOpen) {
      setTimeout(() => {
        setIsMobileFilterOpen(false);

        // Quick scroll to products
        setTimeout(() => {
          const productsSection = document.querySelector(
            ".products__product-container"
          );
          if (productsSection) {
            productsSection.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }, 600);
    }
  };

  // NEW: Handle carcasa filter clicks
  const handleCarcasaFilterClick = (carcasaValue, event) => {
    event.target.classList.toggle("active");

    if (carcasaFilters.includes(carcasaValue)) {
      setCarcasaFilters(carcasaFilters.filter((filter) => filter !== carcasaValue));
    } else {
      setCarcasaFilters([...carcasaFilters, carcasaValue]);
    }

    // Auto-collapse on mobile for carcasa selections
    if (isMobile && isMobileFilterOpen) {
      setTimeout(() => {
        setIsMobileFilterOpen(false);

        // Quick scroll to products
        setTimeout(() => {
          const productsSection = document.querySelector(
            ".products__product-container"
          );
          if (productsSection) {
            productsSection.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }, 600);
    }
  };

  // UPDATED: Handle price range changes with mobile auto-collapse
  const handlePriceChange = (newPriceRange) => {
    setPriceRange(newPriceRange);

    // Auto-collapse on mobile after price changes
    if (isMobile && isMobileFilterOpen) {
      // Clear existing timeout
      if (priceChangeTimeoutRef.current) {
        clearTimeout(priceChangeTimeoutRef.current);
      }

      // Set new timeout for auto-collapse
      priceChangeTimeoutRef.current = setTimeout(() => {
        setIsMobileFilterOpen(false);

        // Smooth scroll to products after a brief delay
        setTimeout(() => {
          const productsSection = document.querySelector(
            ".products__product-container"
          );
          if (productsSection) {
            const yOffset = -20;
            const y =
              productsSection.getBoundingClientRect().top +
              window.pageYOffset +
              yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        }, 200);
      }, 1200);
    }
  };



  useEffect(() => {
    (async () => {
      const response = await getHeroCatalogue();
      if (response !== null) {
        setPageHeroBackground(response);
        setLoadingHero(false);
      } else {
        setLoadingHero(false);
      }
    })();
  }, []);

  // REMOVED: No longer needed since we use direct click handlers



  if (loadingHero || loading) {
    return (
      <div className="catalogue">
        <Container>
          <p className="py-4">Cargando información...</p>
        </Container>
      </div>
    );
  }

  // Determine which products to display with pagination
  const isFiltering = categories.length > 0 || priceRange.min > 0 || priceRange.max < 100000 || caratulaFilters.length > 0 || carcasaFilters.length > 0;



  const displayProducts = (() => {
    if (isFiltering) {
      // Apply pagination to filtered products
      const itemsPerPage = 12;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return filteredProducts.slice(startIndex, endIndex);
    } else {
      // Show current page products when not filtering
      return products;
    }
  })();

  return (
    <section className="catalogue">
      <div
        className="jumbotron"
        style={{
          backgroundImage: `url(${pageHeroBackground?.imagen?.url})`,
        }}
      ></div>
      <Row>
        <section className="filters col-12 col-lg-3">
          <div className="filters__container">
            {/* UPDATED: Mobile-responsive header with React state */}
            <div
              className="filters__header"
              style={{ cursor: isMobile ? "pointer" : "default" }}
              onClick={isMobile ? handleMobileFilterToggle : undefined}
            >
              <img src={FilterIcon} alt="Filtros" className="filters__icon" />
              <p className="filters__nombre">Filtros</p>
              {isMobile && (
                <span
                  className="d-block d-lg-none ms-3"
                  style={{ fontSize: "1.1rem", fontWeight: "bolder" }}
                >
                  {isMobileFilterOpen
                    ? "Toca para ocultar"
                    : "Toca para expandir filtros"}
                </span>
              )}
            </div>

            {/* UPDATED: State-controlled filter container */}
            <div
              className={`filters__filter-container ${isMobile ? (isMobileFilterOpen ? "show" : "") : ""
                }`}
            >
              <div
                className="filters__filter filters__filter--clear filter__item"
                data-filter="all"
              >
                <div className="filters__filter__content">
                  <p
                    className="filters__filter__name filters__filter__name--small"
                    onClick={clearFilters}
                  >
                    Limpiar filtros <i className="far fa-times"></i>
                  </p>
                </div>
              </div>

              {/* Price Range Slider - Only show if 5+ products */}
              {allProducts && allProducts.length >= 5 && (
                <div className="filters__filter filters__filter--price">
                  <PriceRangeSlider
                    products={allProducts}
                    onPriceChange={handlePriceChange}
                    resetTrigger={resetFilters}
                  />
                </div>
              )}

              <div className="filters__filter filters__filter--dropdown">
                <div className="filters__filter__content">
                  <p
                    className="filters__filter__name"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                      const filterElement = e.target.closest('.filters__filter');
                      toggleClass(filterElement, "open");
                      toggleClass(dropdownMenu, "open");
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    Género
                  </p>
                  <div className="filters__filter__dropdown-menu">
                    <ul className="filters__filter__dropdown-menu__item-container">
                      {getUniqueWatchGenres().map((genre, key) => (
                        <li
                          className="filters__filter__dropdown-menu__item filter__item"
                          data-filter={genre.toLowerCase()}
                          key={key}
                          onClick={(e) =>
                            handleFilterClick(genre, "genero", e)
                          }
                        >
                          <p className="filters__filter__dropdown-menu__item__name">
                            {genre}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="filters__filter filters__filter--dropdown">
                <div className="filters__filter__content">
                  <p
                    className="filters__filter__name"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                      const filterElement = e.target.closest('.filters__filter');
                      toggleClass(filterElement, "open");
                      toggleClass(dropdownMenu, "open");
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    Correa
                  </p>
                  <div className="filters__filter__dropdown-menu">
                    <ul className="filters__filter__dropdown-menu__item-container">
                      {getUniqueWatchBands().map((band, key) => (
                        <li
                          className="filters__filter__dropdown-menu__item filter__item"
                          data-filter={band.toLowerCase()}
                          key={key}
                          onClick={(e) =>
                            handleFilterClick(band, "tipoCorrea", e)
                          }
                        >
                          <p className="filters__filter__dropdown-menu__item__name">
                            {band}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="filters__filter filters__filter--dropdown">
                <div className="filters__filter__content">
                  <p
                    className="filters__filter__name"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                      const filterElement = e.target.closest('.filters__filter');
                      toggleClass(filterElement, "open");
                      toggleClass(dropdownMenu, "open");
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    Marca
                  </p>
                  <div className="filters__filter__dropdown-menu">
                    <ul className="filters__filter__dropdown-menu__item-container">
                      {getUniqueBrands().map((brand, key) => (
                        <li
                          className="filters__filter__dropdown-menu__item filter__item"
                          data-filter={brand.toLowerCase()}
                          key={key}
                          onClick={(e) =>
                            handleFilterClick(brand, "marca", e)
                          }
                        >
                          <p className="filters__filter__dropdown-menu__item__name">
                            {brand}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="filters__filter filters__filter--dropdown">
                <div className="filters__filter__content">
                  <p
                    className="filters__filter__name"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                      const filterElement = e.target.closest('.filters__filter');
                      toggleClass(filterElement, "open");
                      toggleClass(dropdownMenu, "open");
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    Condición
                  </p>
                  <div className="filters__filter__dropdown-menu">
                    <ul className="filters__filter__dropdown-menu__item-container">
                      {getUniqueWatchConditions().map((condition, key) => (
                        <li
                          className="filters__filter__dropdown-menu__item filter__item"
                          data-filter={condition.toLowerCase()}
                          key={key}
                          onClick={(e) =>
                            handleFilterClick(condition, "condicion", e)
                          }
                        >
                          <p className="filters__filter__dropdown-menu__item__name">
                            {condition}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="filters__filter filters__filter--dropdown">
                <div className="filters__filter__content">
                  <p
                    className="filters__filter__name"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                      const filterElement = e.target.closest('.filters__filter');
                      toggleClass(filterElement, "open");
                      toggleClass(dropdownMenu, "open");
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    Tipo
                  </p>
                  <div className="filters__filter__dropdown-menu">
                    <ul className="filters__filter__dropdown-menu__item-container">
                      {getUniqueWatchTypes().map((type, key) => (
                        <li
                          className="filters__filter__dropdown-menu__item filter__item"
                          data-filter={type.toLowerCase()}
                          key={key}
                          onClick={(e) =>
                            handleFilterClick(type, "tipo", e)
                          }
                        >
                          <p className="filters__filter__dropdown-menu__item__name">
                            {type}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="filters__filter filters__filter--dropdown">
                <div className="filters__filter__content">
                  <p
                    className="filters__filter__name"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                      const filterElement = e.target.closest('.filters__filter');
                      toggleClass(filterElement, "open");
                      toggleClass(dropdownMenu, "open");
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    Calibre
                  </p>
                  <div className="filters__filter__dropdown-menu">
                    <ul className="filters__filter__dropdown-menu__item-container">
                      {getUniqueWatchCalibers().map((caliber, key) => (
                        <li
                          className="filters__filter__dropdown-menu__item filter__item"
                          data-filter={caliber.toLowerCase()}
                          key={key}
                          onClick={(e) =>
                            handleFilterClick(caliber, "calibre", e)
                          }
                        >
                          <p className="filters__filter__dropdown-menu__item__name">
                            {caliber}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* NEW: Caratula (Dial Color) Filter */}
              <div className="filters__filter filters__filter--dropdown">
                <div className="filters__filter__content">
                  <p
                    className="filters__filter__name"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                      const filterElement = e.target.closest('.filters__filter');
                      toggleClass(filterElement, "open");
                      toggleClass(dropdownMenu, "open");
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    Color de Carátula
                  </p>
                  <div className="filters__filter__dropdown-menu">
                    <ul className="filters__filter__dropdown-menu__item-container">
                      {getUniqueCaratulas().map((caratula, key) => (
                        <li
                          className="filters__filter__dropdown-menu__item filter__item"
                          data-filter={caratula.toLowerCase()}
                          key={key}
                          onClick={(e) =>
                            handleCaratulaFilterClick(caratula, e)
                          }
                        >
                          <p className="filters__filter__dropdown-menu__item__name">
                            {caratula}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* NEW: Carcasa (Case Material) Filter */}
              <div className="filters__filter filters__filter--dropdown">
                <div className="filters__filter__content">
                  <p
                    className="filters__filter__name"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                      const filterElement = e.target.closest('.filters__filter');
                      toggleClass(filterElement, "open");
                      toggleClass(dropdownMenu, "open");
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    Material de Carcasa
                  </p>
                  <div className="filters__filter__dropdown-menu">
                    <ul className="filters__filter__dropdown-menu__item-container">
                      {getUniqueCarcasas().map((carcasa, key) => (
                        <li
                          className="filters__filter__dropdown-menu__item filter__item"
                          data-filter={carcasa.toLowerCase()}
                          key={key}
                          onClick={(e) =>
                            handleCarcasaFilterClick(carcasa, e)
                          }
                        >
                          <p className="filters__filter__dropdown-menu__item__name">
                            {carcasa}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="products col-12 col-lg-9">
          <div className="products__container">
            <div className="products__header">
              <h2 className="products__nombre">Nuestra colección</h2>
              <p className="products__text">
                Disfruta de nuestros ejemplares de los cuales encontrarás una
                excelente calidad entre ellos relojes nuevos, usados, suizos,
                japonenes, vintage, con calibres automáticos, de cuarzo y de
                cuerda. Todos 100% originales, verificados previamente por
                personal capacitado. Y con los mejores precios en el mercado.
              </p>
            </div>
            <div className="products__product-container">
              <Row>
                {loading ? (
                  <p className="pt-3">Cargando productos...</p>
                ) : displayProducts?.length > 0 ? (
                  displayProducts.map((product, key) => (
                    <ProductCard
                      product={product}
                      key={key}
                      loading={loading}
                    />
                  ))
                ) : (
                  <p className="py-3">
                    {categories.length > 0 ||
                      priceRange.min > 0 ||
                      priceRange.max < 100000 ||
                      caratulaFilters.length > 0 ||
                      carcasaFilters.length > 0
                      ? "No se encontraron relojes con los filtros seleccionados."
                      : "No hay productos disponibles."}
                  </p>
                )}
              </Row>

              {/* Show pagination for both filtered and unfiltered results */}
              {!loading && (
                <>

                  <CustomPagination
                    totalPages={isFiltering ? filteredTotalPages : totalPages}
                    currentPage={currentPage}
                    paginate={(page) => {
                      console.log('Pagination clicked:', page);
                      setCurrentPage(page);
                    }}
                    hasNextPage={currentPage < (isFiltering ? filteredTotalPages : totalPages)}
                    hasPrevPage={currentPage > 1}
                  />


                </>
              )}
            </div>
          </div>
        </section>
      </Row>
    </section>
  );
}
